import { createPortal } from 'react-dom';
import dayjs from 'dayjs';
import { useGetBetHistoryQuery } from '@/store/api/betReportApi';
import { Icons } from '@/utils/Icons';

interface UnsettledBetModalProps {
  open: boolean;
  onClose: () => void;
}

const COLS = [
  'Competition',
  'Event',
  'Market',
  'Runner',
  'Side',
  'Line',
  'Rate',
  'Amount',
  'PP',
  'PL',
  'Status',
  'Date',
];

const UnsettledBetModal = ({ open, onClose }: UnsettledBetModalProps) => {
  const { data, isLoading, isFetching } = useGetBetHistoryQuery(
    {
      page: 1,
      limit: 50,
      betTime: 'CURRENT',
      fromDate: dayjs().subtract(15, 'day').startOf('day').toISOString(),
      toDate: dayjs().endOf('day').toISOString(),
    },
    { skip: !open }
  );

  const bets = data?.bets ?? [];
  const loading = isLoading || isFetching;

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-start justify-center bg-black/60 pt-8">
      <div className="bg-white rounded-[6px] shadow-2xl w-[90vw] max-h-[80vh] flex flex-col">
        {/* Header */}
        <div
          className="flex items-center justify-between px-2 py-1 rounded-t"
          style={{ background: 'linear-gradient(#fec733, #fbac40)' }}
        >
          <span className=" text-black text-[16px]">Unsettled Bets</span>
          <button onClick={onClose} className="text-white hover:opacity-70">
            <Icons.Cross2 size={14} />
          </button>
        </div>

        {/* Table */}
        <div className="overflow-auto flex-1 p-2">
          <table className="w-full text-[12px] border-collapse min-w-[900px]">
            <thead>
              <tr
                style={{ background: 'linear-gradient(#fec733, #fbac40)' }}
                className="text-black"
              >
                {COLS.map((col) => (
                  <th
                    key={col}
                    className="text-left px-3 py-1 border border-[#e09518] font-medium whitespace-nowrap"
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="bg-white">
                    {Array.from({ length: COLS.length }).map((__, j) => (
                      <td key={j} className="px-3 py-1.5 border border-gray-200">
                        <div className="h-4 bg-gray-200 animate-pulse rounded w-3/4" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : bets.length === 0 ? (
                <tr>
                  <td colSpan={COLS.length} className="text-center py-1 font-medium text-black">
                    No Data Found
                  </td>
                </tr>
              ) : (
                (bets as any[]).map((bet, i) => {
                  const side = (bet.betOn ?? '').toLowerCase();
                  const rowBg = side === 'back' ? 'bg-[#8EC5FE]' : 'bg-[#FFA0D0]';
                  return (
                    <tr key={bet.id ?? i} className={rowBg}>
                      <td className="px-3 py-1 border border-gray-200 font-medium text-[#d4ac0d]">
                        {bet.competition || bet.event?.competition || bet.sport || '-'}
                      </td>
                      <td className="px-3 py-1 border border-gray-200 max-w-[160px] truncate">
                        {bet.event?.name ?? '-'}
                      </td>
                      <td className="px-3 py-1 border border-gray-200 max-w-[120px] truncate">
                        {bet.marketName ?? '-'}
                      </td>
                      <td className="px-3 py-1 border border-gray-200">{bet.selection ?? '-'}</td>
                      <td
                        className={`px-3 py-1 border border-gray-200 font-medium ${
                          side === 'back' ? 'text-blue-600' : 'text-pink-600'
                        }`}
                      >
                        {side === 'back' ? 'Back' : side === 'lay' ? 'Lay' : side || '-'}
                      </td>
                      <td className="px-3 py-1 border border-gray-200 text-center">
                        {bet.line ?? '-'}
                      </td>
                      <td className="px-3 py-1 border border-gray-200 text-center">
                        {bet.odds ?? '-'}
                      </td>
                      <td className="px-3 py-1 border border-gray-200 text-right">
                        {bet.amount != null ? Number(bet.amount).toFixed(2) : '-'}
                      </td>
                      <td className="px-3 py-1 border border-gray-200 text-right text-green-700 font-semibold">
                        {bet.potentialProfit != null ? Number(bet.potentialProfit).toFixed(2) : '-'}
                      </td>
                      <td className="px-3 py-1 border border-gray-200 text-right text-red-600 font-semibold">
                        {bet.liability != null ? Number(bet.liability).toFixed(2) : '-'}
                      </td>
                      <td className="px-3 py-1 border border-gray-200 whitespace-nowrap">
                        {bet.status ?? '-'}
                      </td>
                      <td className="px-3 py-1 border border-gray-200 whitespace-nowrap">
                        {bet.placedAt ? dayjs(bet.placedAt).format('D/M/YYYY, h:mm:ss a') : '-'}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="flex justify-center py-3 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-3 py-1 border border-red-500 text-red-500 rounded text-[13px] hover:bg-red-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default UnsettledBetModal;
